import { useTranslation } from 'react-i18next';
import moment from 'moment/moment';
import MainScreenButton from '../../../../components/forms/mainScreenButton';
import clsx from 'clsx';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { useMainScreenButtonStyles } from '../../../../components/forms/mainScreenButtonStyles';
import { MainButtonBackground } from '../formsUtil';
import { hasOneOfPermission, Permissions } from '../../../../store/reducers/user';
import { useDispatch } from 'react-redux';
import { setSnackbar } from '../../../../store/reducers/globals';
import { setLoading } from '../../../../store/reducers/additionalData';
import { openPatientForm } from '../../../../store/reducers/patientContext';
import { getAcuityPriority, getTriageButtonColorMatrix } from './triageUtils';
import { getConfig } from '../../../../util/configUtil';

const useTriageStyles = makeStyles((theme) => ({
    accordion: {
        backgroundColor: theme.palette.accordion.backgroundColor,
    },
    colorSystem: {
        width: 16,
        height: 16,
        backgroundColor: 'white',
        display: 'inline-block',
        marginRight: 10,
        verticalAlign: 'middle',
        border: '1px solid #222',
    },
    colorSystemTextBox: {
        width: 16,
        height: 16,
        backgroundColor: 'white',
        display: 'inline-block',
        marginRight: 5,
        marginLeft: 5,
        verticalAlign: 'middle',
        border: '1px solid #222',
    },
    discriminator: {
        display: 'inline-block',
        verticalAlign: 'middle',
    },
    dialogMainContent: {
        paddingTop: '10px !important',
        '&.header': {
            paddingBottom: 0,
        },
    },
    snomedCode: {
        marginRight: 10,
        padding: '2px 7px',
        backgroundColor: theme.palette.background.default,
        borderRadius: 10,
        fontSize: '80%',
    },
    sensitiveDescr: {
        marginLeft: 10,
        padding: '2px 7px',
        backgroundColor: theme.palette.background.default,
    },

    triageSymbol: {
        position: 'relative',
        verticalAlign: 'middle',
        display: 'inline-block',
        width: 15,
        height: 15,
        marginTop: -2,
        marginRight: 10,
        border: '1px solid ' + theme.palette.grey[400],
    },
    triageTime: {
        fontWeight: 'normal',
        marginLeft: 10,
        fontSize: '90%',
        display: 'inline-block',
        opacity: 0.7,
        whiteSpace: 'break-spaces',
    },
    seenDlg: {
        width: '100%',
        '& .label-set': {
            textTransform: 'none !important',
            '& .label-part1': {
                textTransform: 'uppercase',
            },
            '& .label-part2': {
                fontWeight: 'normal',
                margin: '0 10px',
                wordBreak: 'break-word',
            },
            '& .label-part3': {
                // fontSize: "90%",
                opacity: 0.7,
                whiteSpace: 'break-spaces',
            },
        },
        '& .not-set': {},
        '& .not-set-not-mandatory': {},
    },
    triageDescription: {
        display: 'flex',
        flexDirection: 'column',
        paddingLeft: 10,
        marginTop: 0,
        borderRadius: 8,
    },
}));

function TriageButton(props) {
    const theme = useTheme();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const classesLocal = useTriageStyles();
    const classesButton = useMainScreenButtonStyles();

    // Configs
    const defaultDateTimeFormat = getConfig('defaultDateTimeFormat');

    // Permission
    const canSeeTriage = dispatch(hasOneOfPermission([Permissions.AGYLE_CAN_VIEW_TRIAGE, Permissions.AGYLE_CAN_MANAGE_TRIAGE]));

    const triageDone = () => {
        return props.value && props.value.discriminator && props.value.when;
    };
    const triageTimeStamp = () => {
        return props.value && !props.value.discriminator && props.value.triageClinicians;
    };

    const getTextForPriority = (triage) => {
        let description = '';
        if (triage?.discriminator) {
            const priority = getAcuityPriority(triage?.discriminator);

            switch (priority) {
                case 1:
                    description = t('Immediate');
                    break;
                case 2:
                    description = t('Very Urgent');
                    break;
                case 3:
                    description = t('Urgent');
                    break;
                case 4:
                    description = t('Standard');
                    break;
                case 5:
                    description = t('Not Urgent');
                    break;
                default:
                    break;
            }
        }
        return description;
    };

    const getButtonBackground = (textColor) => {
        if (textColor) {
            return triageTimeStamp() ? (theme.palette.mode !== 'light' ? '#eff1f3' : '#222425') : '#000000';
        }

        if (triageTimeStamp()) {
            return theme.palette.mode === 'light' ? '#ffffff' : '#222425';
        }
        const priority = getAcuityPriority(props.value?.discriminator);
        return priority === 5 ? '#ffffff' : getTriageButtonColorMatrix(props.value?.discriminator);
    };

    const openTriageForm = () => {
        dispatch(setLoading(true));
        setTimeout(() => {
            dispatch(openPatientForm('triage'));
        }, 1);
    };

    const handleOpenTriage = () => {
        if (canSeeTriage) {
            openTriageForm();
        } else
            dispatch(
                setSnackbar({
                    type: 'warning',
                    message: t('User has no permission to see triage!'),
                }),
            );
    };

    const triageButtonTimeStamp = () => {
        return (
            <MainScreenButton
                label={t(`Triage ${!triageDone() ? t('') : ''}`)}
                colorType="notMandatory"
                filled={!!props.filled}
                filledTextContainerBackground={getButtonBackground()}
                filledTextContainerFontColor={getButtonBackground(true)}
                color={props.color}
                value={props.value}
                datePath={props.datePath}
                onOpen={handleOpenTriage}
                disabled={props.disabled || !canSeeTriage}
                spacialRender={() => {
                    return (
                        triageTimeStamp() && (
                            <div className={clsx(classesButton.formFilledDetails, classesLocal.triageDescription)}>
                                <Typography variant="subtitle1" fontWeight="normal">
                                    <div className={classesLocal.seenDlg}>
                                        <div className="label-set" style={{ display: 'flex', flexDirection: 'column', paddingBottom: 0 }}>
                                            <div style={{ marginBottom: 0, width: '100%' }}>
                                                {props.majorTrauma && 'MAJOR TRAUMA, '}
                                                <span className="label-part2" style={{ marginLeft: props.majorTrauma ? 5 : 0 }}>
                                                    Time Stamp <br /> {props.value?.who}
                                                </span>

                                                <span className="label-part3">
                                                    {moment(props.value?.when).format(defaultDateTimeFormat)}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </Typography>
                            </div>
                        )
                    );
                }}
            />
        );
    };

    const triageButton = () => {
        return (
            <MainScreenButton
                label={t(`Triage ${!triageDone() ? t('') : ''}`)}
                customColor={triageDone() ? 'inherit' : props.color}
                colorType="notMandatory"
                btnBackground={() => (triageDone() ? '' : MainButtonBackground.RED)}
                filledTextContainerBackground={getButtonBackground()}
                filledTextContainerFontColor={getButtonBackground(true)}
                filled={!!props.filled}
                value={props.value}
                datePath={props.datePath}
                onOpen={handleOpenTriage}
                disabled={props.disabled || !canSeeTriage}
                spacialRender={() => {
                    return (
                        triageDone() && (
                            <div className={clsx(classesButton.formFilledDetails, classesLocal.triageDescription)}>
                                <Typography variant="subtitle1" fontWeight="normal">
                                    {props.majorTrauma && <span style={{ marginRight: 5 }}>MAJOR TRAUMA -</span>}
                                    {triageDone() && <>{getTextForPriority(props.value)}</>}
                                </Typography>

                                <div style={{ width: '100%' }}>
                                    <Typography variant="subtitle1" fontWeight="normal">
                                        {props.value && props.value.who}
                                        <div className={classesLocal.triageTime}>
                                            {props.value && moment(props.value.when).format(defaultDateTimeFormat)}
                                        </div>
                                    </Typography>
                                </div>
                            </div>
                        )
                    );
                }}
            ></MainScreenButton>
        );
    };
    return triageTimeStamp() ? triageButtonTimeStamp() : triageButton();
}

export default TriageButton;
